/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSelector, Selector } from 'reselect';
import { _DeepReadonlyObject } from 'utility-types/dist/mapped-types';

import {
  selectCurrentCourseLinkedPortalJS,
  selectPortalsByPath,
  getCourseLinkedPortal,
  getCourseLinkedPortalJS,
} from '../Portals/selectors';
import {
  selectCurrentCourseThemeData,
  coursesSelector,
} from '../Courses/selectors';
import {
  COURSE_THEME_DEFAULT_THEME_COLOR,
  COURSE_THEME_DEFAULT_BACKGROUND_IMAGE,
  COURSE_THEME_DEFAULT_BACKGROUND_COLOR,
  COURSE_THEME_DEFAULT_BACKGROUND_IMAGE_REPEAT,
} from '../../web/containers/CourseTheme/constants';
import { ReduxState } from '../state';
import { CourseThemeDataFull, CourseThemeData } from '../Courses/types';
import { Portal, PortalThemeData } from '../Portals/types';
import {
  selectCourseApperanceSettings,
  selectIsOnCourseAppearanceSetting,
} from '../CourseAppearanceSettings/selectors';

const getCourseThemeDataWithPortal = (
  courseThemeData: CourseThemeDataFull | null,
  portalThemeData: PortalThemeData | null
): { [key: string]: string } | null => {
  if (portalThemeData && 'brand-color' in portalThemeData) {
    const { 'brand-color': brandColor } = portalThemeData;
    return {
      ...(brandColor ? { 'theme-color': brandColor } : {}),
      ...courseThemeData,
    };
  } else {
    return courseThemeData;
  }
};

const getCourseThemeDataWithPortalWithAppearanceSettings = (
  courseThemeDataWithPortal: { [key: string]: string } | null,
  courseAppearanceSettings: _DeepReadonlyObject<Partial<CourseThemeDataFull>>,
  isOnCourseAppearanceSetting: boolean
): CourseThemeData | null => {
  if (isOnCourseAppearanceSetting) {
    return {
      ...courseThemeDataWithPortal,
      ...courseAppearanceSettings,
    };
  } else {
    return courseThemeDataWithPortal;
  }
};

const getPortalCourseTheme = (
  courseThemeDataWithPortalAndAppearanceSettings: CourseThemeData | null
): any => {
  const themeColor =
    (courseThemeDataWithPortalAndAppearanceSettings &&
      courseThemeDataWithPortalAndAppearanceSettings['theme-color']) ||
    COURSE_THEME_DEFAULT_THEME_COLOR;
  const backgroundImage =
    (courseThemeDataWithPortalAndAppearanceSettings &&
      courseThemeDataWithPortalAndAppearanceSettings['background-image']) ||
    COURSE_THEME_DEFAULT_BACKGROUND_IMAGE;
  const backgroundColor =
    (courseThemeDataWithPortalAndAppearanceSettings &&
      courseThemeDataWithPortalAndAppearanceSettings['background-color']) ||
    COURSE_THEME_DEFAULT_BACKGROUND_COLOR;
  const backgroundImageRepeat =
    (courseThemeDataWithPortalAndAppearanceSettings &&
      courseThemeDataWithPortalAndAppearanceSettings[
        'background-image-repeat'
      ]) ||
    COURSE_THEME_DEFAULT_BACKGROUND_IMAGE_REPEAT;

  return {
    ...courseThemeDataWithPortalAndAppearanceSettings,
    'theme-color': themeColor,
    'background-image': backgroundImage,
    'background-color': backgroundColor,
    'background-image-repeat': backgroundImageRepeat,
  };
};

const getCourseLinkedPortalTheme = (portal: Portal | null): any => {
  const { themeData } = portal || {};

  return themeData || null;
};

export const selectCurrentCourseLinkedPortalTheme = createSelector(
  selectCurrentCourseLinkedPortalJS,
  (currentPortal) => {
    return getCourseLinkedPortalTheme(currentPortal);
  }
);

export const selectCurrentCourseIsUsingPortalTheme: Selector<
  ReduxState,
  boolean
> = createSelector([selectCurrentCourseThemeData], (courseThemeData) => {
  return !(courseThemeData && 'theme-color' in courseThemeData);
});

export const selectCurrentCourseThemeDataWithPortal: Selector<
  ReduxState,
  { [key: string]: string } | null
> = createSelector(
  [selectCurrentCourseThemeData, selectCurrentCourseLinkedPortalTheme],
  (
    courseThemeData: CourseThemeDataFull | null,
    portalThemeData: PortalThemeData | null
  ) => {
    return getCourseThemeDataWithPortal(courseThemeData, portalThemeData);
  }
);

export const selectCurrentCourseThemeDataWithPortalWithAppearanceSettings: Selector<
  ReduxState,
  CourseThemeData | null
> = createSelector(
  [
    selectCurrentCourseThemeDataWithPortal,
    selectCourseApperanceSettings,
    selectIsOnCourseAppearanceSetting,
  ],
  (
    currentCourseThemeDataWithPortal,
    courseAppearanceSettings,
    isOnCourseAppearanceSetting
  ) => {
    return getCourseThemeDataWithPortalWithAppearanceSettings(
      currentCourseThemeDataWithPortal,
      courseAppearanceSettings,
      isOnCourseAppearanceSetting
    );
  }
);

export const selectCurrentPortalCourseTheme: Selector<
  ReduxState,
  CourseThemeDataFull
> = createSelector(
  [selectCurrentCourseThemeDataWithPortalWithAppearanceSettings],
  (currentCourseThemeDataWithPortalAndAppearanceSettings) => {
    return getPortalCourseTheme(
      currentCourseThemeDataWithPortalAndAppearanceSettings
    );
  }
);

export const selectPortalCourseThemeByCoursePath = (
  coursePath: string | null
): Selector<ReduxState, CourseThemeDataFull> =>
  createSelector(
    [
      coursesSelector,
      selectPortalsByPath,
      selectCourseApperanceSettings,
      selectIsOnCourseAppearanceSetting,
    ],
    (
      courses,
      portalsByPath,
      courseAppearanceSettings,
      isOnCourseAppearanceSetting
    ) => {
      let courseThemeDataWithPortalAndAppearanceSettings: Partial<CourseThemeDataFull> | null = null;
      const course = courses.get(coursePath);
      if (course) {
        const courseThemeData = course.get('themeData');
        const courseThemeDataJS = courseThemeData
          ? courseThemeData.toJS()
          : null;
        const currentCourseLinkedPortal = getCourseLinkedPortal(
          course,
          portalsByPath
        );
        const currentCourseLinkedPortalJS = getCourseLinkedPortalJS(
          currentCourseLinkedPortal
        );
        const portalThemeData = getCourseLinkedPortalTheme(
          currentCourseLinkedPortalJS
        );
        const finalThemeData = getCourseThemeDataWithPortal(
          courseThemeDataJS,
          portalThemeData
        );
        courseThemeDataWithPortalAndAppearanceSettings = getCourseThemeDataWithPortalWithAppearanceSettings(
          finalThemeData,
          courseAppearanceSettings,
          isOnCourseAppearanceSetting
        );
      }

      return getPortalCourseTheme(
        courseThemeDataWithPortalAndAppearanceSettings
      );
    }
  );
